import { useState, useEffect, useRef } from 'react';

interface TimerStorage {
  endTime: number;
  duration: number;
}

interface UseTimerResult {
  timeRemaining: number;
  startTimer: (duration: number) => void;
  resetTimer: () => void;
  isTimerRunning: boolean;
}

const TIMER_STORAGE_KEY = 'myAppTimer';

const calculateTimeLeft = (endTime: number): number =>
  Math.max(0, Math.floor((endTime - Date.now()) / 1000));

const loadTimerFromStorage = (): TimerStorage | null => {
  const saved = localStorage.getItem(TIMER_STORAGE_KEY);
  if (!saved) return null;
  return JSON.parse(saved) as TimerStorage;
};

const saveTimerToStorage = (endTime: number, duration: number): void => {
  localStorage.setItem(TIMER_STORAGE_KEY, JSON.stringify({ endTime, duration }));
};

const clearTimerStorage = (): void => {
  localStorage.removeItem(TIMER_STORAGE_KEY);
};

const useTimer = (initialDuration: number = 120): UseTimerResult => {
  const [timeRemaining, setTimeRemaining] = useState<number>(() => {
    const saved = loadTimerFromStorage();
    if (!saved) return initialDuration;

    const timeLeft = calculateTimeLeft(saved.endTime);
    return timeLeft > 0 ? timeLeft : initialDuration;
  });

  const [isTimerRunning, setIsTimerRunning] = useState<boolean>(() => {
    const saved = loadTimerFromStorage();
    if (!saved) return false;
    return calculateTimeLeft(saved.endTime) > 0;
  });

  const timerIdRef = useRef<NodeJS.Timeout | null>(null);
  const durationRef = useRef<number>(initialDuration);

  const stopInterval = (): void => {
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }
  };

  const tick = (endTime: number): void => {
    stopInterval();

    timerIdRef.current = setInterval(() => {
      setTimeRemaining(() => {
        const timeLeft = calculateTimeLeft(endTime);

        if (timeLeft <= 0) {
          stopInterval();
          setIsTimerRunning(false);
          clearTimerStorage();
          return 0;
        }

        return timeLeft;
      });
    }, 1000);
  };

  const startTimer = (duration: number): void => {
    durationRef.current = duration;
    const endTime = Date.now() + duration * 1000;

    saveTimerToStorage(endTime, duration);
    setTimeRemaining(duration);
    setIsTimerRunning(true);
    tick(endTime);
  };

  const resetTimer = (): void => {
    stopInterval();
    setIsTimerRunning(false);
    setTimeRemaining(durationRef.current);
    clearTimerStorage();
  };

  useEffect(() => {
    const saved = loadTimerFromStorage();
    if (!saved) return;

    const timeLeft = calculateTimeLeft(saved.endTime);

    if (timeLeft <= 0) {
      clearTimerStorage();
      return;
    }

    durationRef.current = saved.duration;
    setTimeRemaining(timeLeft);
    setIsTimerRunning(true);
    tick(saved.endTime);

    return stopInterval;
  }, []);

  return { timeRemaining, startTimer, resetTimer, isTimerRunning };
};

export default useTimer;
